import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { AppPicker } from './AppPicker.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface DeploymentRow {
  readonly deploymentId: string;
  readonly deploymentCode: string;
  readonly applicationId: string;
  readonly versionNo: number;
  readonly sourceEnvironment: string;
  readonly targetEnvironment: string;
  readonly deploymentStatus: string;
  readonly approvalRequired: boolean;
  readonly approvedBy: string | null;
  readonly deployedBy: string | null;
}

const ENVIRONMENTS = ['TEST', 'UAT', 'PROD'];
const STATUSES = ['PENDING', 'APPROVED', 'DEPLOYED', 'FAILED', 'ROLLED_BACK'];
const PAGE = 25;

/**
 * M40 — Deployment Console (§18). The promotion surface over T531. Request a
 * deployment to the next environment, then approve (BR-01 / BR-06 gate on
 * UAT/PROD), execute, or roll back (BR-04). Viewing needs DEPLOYMENT_VIEW;
 * request/execute need APP_DEPLOY; approve needs DEPLOYMENT_APPROVE; rollback
 * needs APP_ROLLBACK — a strict separation of duties.
 */
export function DeploymentConsole(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [applicationId, setApplicationId] = useState('');
  const [envFilter, setEnvFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<DeploymentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [requestAppId, setRequestAppId] = useState('');
  const [targetEnvironment, setTargetEnvironment] = useState('TEST');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (applicationId) params.set('applicationId', applicationId);
    if (envFilter) params.set('targetEnvironment', envFilter);
    if (statusFilter) params.set('deploymentStatus', statusFilter);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: DeploymentRow[]; total: number }>(`/v1/app-platform/deployments?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, applicationId, envFilter, statusFilter, page, reload]);

  const request = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!requestAppId.trim()) { setActionError(t('m40.common.appIdRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: DeploymentRow }>(`/v1/app-platform/deployments`, {
        method: 'POST',
        body: { applicationId: requestAppId.trim(), targetEnvironment },
        token,
      });
      setNotice(t('m40.deployment.requested', { code: r.data.deploymentCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.deployment.notAuthRequest'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const act = async (
    row: DeploymentRow,
    verb: 'approve' | 'execute' | 'rollback',
    permission: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/deployments/${row.deploymentId}/${verb}`, { method: 'POST', token });
      setNotice(t(`m40.deployment.${verb}Succeeded`, { code: row.deploymentCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.deployment.notAuthAct', { verb, permission }));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="deployment-console">
      <h1>{t('m40.deployment.title')}</h1>

      <section aria-label="request-deployment">
        <h2>{t('m40.deployment.requestHeading')}</h2>
        <form aria-label="deployment-form" onSubmit={(e) => void request(e)}>
          <AppPicker label={t('m40.common.applicationId')} value={requestAppId} onPick={setRequestAppId} testId="field-request-app-id" />
          <label>{t('m40.deployment.targetEnv')}
            <select aria-label="field-target-environment" value={targetEnvironment} onChange={(e) => setTargetEnvironment(e.target.value)}>
              {ENVIRONMENTS.map((env) => <option key={env} value={env}>{env}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-deployment" disabled={busy}>{busy ? t('m40.deployment.requesting') : t('m40.deployment.requestBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="deployment-error">{actionError}</p>}
        {notice && <p aria-label="deployment-notice">{notice}</p>}
      </section>

      <section aria-label="deployment-filters">
        <AppPicker label={t('m40.common.applicationId')} value={applicationId} onPick={(id) => { setApplicationId(id); setPage(0); }} testId="filter-application-id" />
        <label>{t('m40.common.environment')}
          <select aria-label="filter-environment" value={envFilter} onChange={(e) => { setEnvFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m40.deployment.allEnvironments')}</option>
            {ENVIRONMENTS.map((env) => <option key={env} value={env}>{env}</option>)}
          </select>
        </label>
        <label>{t('m40.common.status')}
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m40.common.allStates')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m40.common.loading')}</p> : (
        <>
          <p aria-label="deployment-total">{t('m40.deployment.total', { total })}</p>
          <DataTable<DeploymentRow>
            ariaLabel="deployment-table"
            testId="deployment-table"
            rowKey={(r) => r.deploymentId}
            rowDataAttrs={(r) => ({ 'data-deployment': r.deploymentCode })}
            emptyMessage={t('m40.deployment.empty')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m40.common.code'), render: (r) => r.deploymentCode },
              { key: 'version', header: t('m40.common.version'), render: (r) => `v${r.versionNo}` },
              { key: 'source', header: t('m40.deployment.colSource'), render: (r) => r.sourceEnvironment },
              { key: 'target', header: t('m40.deployment.colTarget'), render: (r) => r.targetEnvironment },
              { key: 'status', header: t('m40.common.status'), cellAriaLabel: (r) => `status-${r.deploymentCode}`, render: (r) => r.deploymentStatus },
              { key: 'actions', header: t('m40.common.actions'), render: (r) => (
                <>
                  <Button variant="secondary" type="button" aria-label={`approve-${r.deploymentCode}`} disabled={r.deploymentStatus !== 'PENDING'} onClick={() => void act(r, 'approve', 'DEPLOYMENT_APPROVE')}>{t('m40.deployment.btnApprove')}</Button>
                  <Button variant="secondary" type="button" aria-label={`execute-${r.deploymentCode}`} disabled={r.deploymentStatus !== 'APPROVED'} onClick={() => void act(r, 'execute', 'APP_DEPLOY')}>{t('m40.deployment.btnExecute')}</Button>
                  <Button variant="danger" type="button" aria-label={`rollback-${r.deploymentCode}`} disabled={r.deploymentStatus !== 'DEPLOYED'} onClick={() => void act(r, 'rollback', 'APP_ROLLBACK')}>{t('m40.common.rollback')}</Button>
                </>
              ) },
            ]}
          />

          <nav aria-label="deployment-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m40.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m40.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m40.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
